import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Card, Table, Badge, Form, InputGroup, Spinner, Pagination } from 'react-bootstrap';
import { LuSearch, LuFilter, LuChevronRight, LuCalendar, LuPackage } from "react-icons/lu";
import { getMyOrders } from '../../redux/reducers/portalOrderSlice';
import moment from 'moment';
import { formatCurrency } from '../../utils/currency';

const PortalOrders = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { businessId } = useParams();
    const { orders, loading, pagination } = useSelector((state) => state.portalOrder);
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const currency = useSelector((state) => state.portalAuth?.businessData?.jssettings?.currency || 'CAD');

    useEffect(() => {
        dispatch(getMyOrders({ page, status: statusFilter !== 'all' ? statusFilter : '' }));
    }, [dispatch, page, statusFilter]);

    const getStatusColor = (status) => {
        const colors = {
            'PENDING_PAYMENT': { bg: '#fef3c7', text: '#f59e0b' },
            'PAID': { bg: '#e0f2fe', text: '#0ea5e9' },
            'ACCEPTED': { bg: '#d1fae5', text: '#10b981' },
            'PROCESSING': { bg: '#ede9fe', text: '#6366f1' },
            'SHIPPED': { bg: '#e0f7fa', text: '#28B0C4' },
            'DELIVERED': { bg: '#d1fae5', text: '#10b981' },
            'CANCELLED': { bg: '#f1f5f9', text: '#64748b' },
            'REJECTED': { bg: '#fee2e2', text: '#ef4444' },
            'COMPLETED': { bg: '#d1fae5', text: '#10b981' },
        };
        return colors[status] || { bg: '#f1f5f9', text: '#64748b' };
    };

    return (
        <div>
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4 gap-3">
                <div>
                    <h2 className="fw-bold text-dark mb-1">My Orders</h2>
                    <p className="text-muted mb-0">Track and manage your order history.</p>
                </div>
                
                <div className="d-flex gap-2">
                    <Form.Select 
                        className="rounded-3 shadow-sm border-0 py-2 ps-3" 
                        style={{ width: '180px' }}
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    >
                        <option value="all">All Status</option>
                        <option value="PENDING_PAYMENT">Pending Payment</option>
                        <option value="PAID">Paid</option>
                        <option value="ACCEPTED">Accepted</option>
                        <option value="PROCESSING">Processing</option>
                        <option value="SHIPPED">Shipped</option>
                        <option value="DELIVERED">Delivered</option>
                    </Form.Select>
                </div>
            </div>

            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Body className="p-0">
                    {loading && !orders.length ? (
                        <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>
                    ) : orders.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4 py-3 text-muted small fw-bold">ORDER #</th>
                                        <th className="py-3 text-muted small fw-bold">DATE</th>
                                        <th className="py-3 text-muted small fw-bold text-center">ITEMS</th>
                                        <th className="py-3 text-muted small fw-bold">TOTAL</th>
                                        <th className="py-3 text-muted small fw-bold">STATUS</th>
                                        <th className="pe-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order) => {
                                        const colors = getStatusColor(order.status);
                                        return (
                                            <tr key={order._id} className="cursor-pointer" onClick={() => navigate(`/customer-side/${businessId}/orders/${order._id}`)}>
                                                <td className="ps-4 py-3">
                                                    <div className="fw-bold text-dark">{order.orderNumber}</div>
                                                </td>
                                                <td className="py-3">
                                                    <div className="d-flex align-items-center gap-2 text-muted small">
                                                        <LuCalendar size={14} />
                                                        {moment(order.createdAt).format('MMM D, YYYY')}
                                                    </div>
                                                </td>
                                                <td className="py-3 text-center text-muted small">
                                                    {order.items?.length || 0}
                                                </td>
                                                <td className="py-3 fw-bold text-dark">
                                                    {formatCurrency(order.totals?.total, currency)}
                                                </td>
                                                <td className="py-3">
                                                    {(() => {
                                                        const s = order.status?.toUpperCase();
                                                        switch (s) {
                                                            case "PAID":
                                                            case "DELIVERED":
                                                                return <Badge bg="success-subtle" className="text-success rounded-pill px-3 py-1">{s === 'PAID' ? 'Paid' : 'Delivered'}</Badge>;
                                                            case "PENDING_PAYMENT":
                                                                return <Badge bg="warning-subtle" className="text-warning rounded-pill px-3 py-1">Pending Payment</Badge>;
                                                            case "ACCEPTED":
                                                                return <Badge bg="info-subtle" className="text-info rounded-pill px-3 py-1">Accepted</Badge>;
                                                            case "PROCESSING":
                                                                return <Badge bg="primary-subtle" className="text-primary rounded-pill px-3 py-1">Processing</Badge>;
                                                            case "SHIPPED":
                                                                return <Badge bg="info-subtle" className="text-info rounded-pill px-3 py-1">Shipped</Badge>;
                                                            case "CANCELLED":
                                                                return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3 py-1">Cancelled</Badge>;
                                                            case "REJECTED":
                                                                return <Badge bg="danger-subtle" className="text-danger rounded-pill px-3 py-1">Rejected</Badge>;
                                                            default:
                                                                return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3 py-1">{order.status?.replace('_', ' ')}</Badge>;
                                                        }
                                                    })()}
                                                </td>
                                                <td className="pe-4 py-3 text-end">
                                                    <LuChevronRight size={18} className="text-muted" />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <LuPackage size={48} className="text-muted opacity-25 mb-3" />
                            <h5 className="text-muted">No orders found</h5>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {pagination?.pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
                        {[...Array(pagination.pages)].map((_, i) => (
                            <Pagination.Item 
                                key={i + 1} 
                                active={i + 1 === page}
                                onClick={() => setPage(i + 1)}
                                style={{ '--bs-pagination-active-bg': '#28B0C4', '--bs-pagination-active-border-color': '#28B0C4' }}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={page === pagination.pages} onClick={() => setPage(page + 1)} />
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default PortalOrders;
